import type {
    OverviewData,
    RespirationDataPoint,
    SwimSpeedDataPoint,
    TiltDataPoint,
} from '@/domain/behavior/types';

import type { CombinedBehaviorExport, EnvironmentalExportSnapshot } from '../types';

function mapEnvironmentalSnapshot(
    overviewData: OverviewData | undefined
): EnvironmentalExportSnapshot {
    const env = overviewData?.environmentalFactors;
    return {
        // The site's wall clock as the card shows it; a `Z` would claim an instant it is not.
        lastMeasurement: env?.lastMeasurement || null,
        oxygenSaturation: env?.oxygenSaturation ?? null,
        oxygenConcentration: env?.oxygenConcentration ?? null,
        salinity: env?.salinity ?? null,
        temperature: env?.temperature ?? null,
        depth: env?.depth ?? null,
    };
}

/**
 * Daily chart series stay on a date grid. Environmental card values are a single
 * latest hourly bucket, so they are returned as a separate one-row snapshot.
 */
export function combineBehaviorExportData(
    swimSpeedData: SwimSpeedDataPoint[],
    respirationData: RespirationDataPoint[],
    tiltData: TiltDataPoint[],
    overviewData: OverviewData | undefined
): CombinedBehaviorExport {
    const swimSpeedMap = new Map(swimSpeedData.map((item) => [item.day, item]));
    const respirationMap = new Map(respirationData.map((item) => [item.day, item]));
    const tiltMap = new Map(tiltData.map((item) => [item.day, item]));

    const allDates = new Set<string>([
        ...swimSpeedData.map((item) => item.day),
        ...respirationData.map((item) => item.day),
        ...tiltData.map((item) => item.day),
    ]);

    const snapshotDay =
        allDates.size > 0 ? [...allDates].sort((a, b) => a.localeCompare(b)).at(-1) : undefined;

    const dailyRows: CombinedBehaviorExport['dailyRows'] = [];

    for (const day of allDates) {
        const isSnapshotDay = day === snapshotDay;
        const swimSpeed = swimSpeedMap.get(day);
        const respiration = respirationMap.get(day);
        const tilt = tiltMap.get(day);

        dailyRows.push({
            day,
            swimSpeed: swimSpeed?.swimSpeed ?? null,
            activeSwimSpeed: swimSpeed?.activeSwimSpeed ?? null,
            respiration: respiration?.respiration ?? null,
            activePercent: respiration?.activePercent ?? null,
            passivePercent: respiration?.passivePercent ?? null,
            oxygenPercent: respiration?.oxygen ?? null,
            tilt: tilt?.tilt ?? null,
            temperature: swimSpeed?.temperature ?? tilt?.temperature ?? null,
            depth: swimSpeed?.depth ?? tilt?.depth ?? null,
            averageSwimSpeed: isSnapshotDay ? (overviewData?.averages.swimSpeed ?? null) : null,
            averageRespiration: isSnapshotDay
                ? (overviewData?.averages.activeRespiration ?? null)
                : null,
            averageTilt: isSnapshotDay ? (overviewData?.averages.tilt ?? null) : null,
        });
    }

    return {
        dailyRows: dailyRows.sort((a, b) => a.day.localeCompare(b.day)),
        environmentalSnapshot: mapEnvironmentalSnapshot(overviewData),
    };
}
